import cn from 'classnames';
import { capitalize } from 'inflected';
import { Appearance, Design, Placement, Size, StretchMode } from '@Components/designConstants';
import '@Pages/GeoMMM/ScenarioPlanning/Modals/ChangeImpactModal.scss';
import { useCallback, useMemo, useState, ReactElement } from 'react';
import { Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import {
  MdArrowDownward,
  MdArrowUpward,
  MdClose,
  MdOutlineFileDownload,
  MdSettings,
  MdSwapVert
} from 'react-icons/md';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Button } from '@Components/Button';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Dropdown } from '@Components/Dropdown';
import { FilterBar } from '@Components/FilterBar/FilterBar';
import { ToggleSwitch } from '@Components/ToggleSwitch/ToggleSwitch';
import { CellRenderer, StickyTable } from '@Components/StickyTable/StickyTable';

import {
  getCostMetricLabel,
  getEffectLabel,
  getModelRevenueWeight,
  getVisibleNodes,
  GROUP_BY_ENUM,
  GROUP_BY_EXPAND_DEPTH_MAP,
  GROUP_BY_OPTIONS,
  MODEL_TYPES,
  ModelRevenueBreakdown,
  ScenarioNode,
  SORT_BY_ENUM,
  SORT_DIRECTION_ENUM,
  stripDcDgMarker,
  sumWeightedByModel,
  HroasDisplayWarning,
  HroasSpModel
} from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningUtils';
import { CellDetailTooltip } from '@Pages/GeoMMM/ScenarioPlanning/Modals/ChangeImpact/CellDetailTooltip';
import {
  abbreviateValue,
  BASE_ROW_HEIGHT,
  buildColumnDefs,
  buildMetricValues,
  buildMidTopHeaders,
  buildSuperTopHeaders,
  buildTopHeaders,
  CellTooltipData,
  collectLeafEntries,
  ColumnConfig,
  CompareTarget,
  CURRENCY_PREFIX,
  dedupeHierarchyParts,
  DEFAULT_COLUMN_CONFIG,
  DeltaRow,
  DIMENSION_COLUMN_WIDTH,
  DIMENSION_SORT_INDEX,
  getDimensionColumnHeaders,
  getIntensityColor,
  HEADER_HEIGHT,
  LEFT_COLUMN_WIDTH,
  MetricValues,
  MIN_CHANGE_THRESHOLD,
  MIN_COLUMN_WIDTH,
  REVENUE_SPLIT,
  REVENUE_SPLIT_LABEL,
  SUPER_TOP_HEIGHT,
  suppressSubThresholdModelNoise,
  toPrettyPercent
} from '@Pages/GeoMMM/ScenarioPlanning/Modals/ChangeImpact/ChangeImpactUtils';
import { FlightingChangesTable } from '@Pages/GeoMMM/ScenarioPlanning/Modals/ChangeImpact/FlightingChangesTable';
import { OverviewMetric } from '@Pages/GeoMMM/ScenarioPlanning/Modals/ChangeImpact/OverviewMetric';
import { useLocalStorageState } from '@src/utils/hooks/useData';
import { downloadJSONToCSV } from '@src/utils/download-utils';
import { Neutral500 } from '@src/utils/colors';

export interface ChangeImpactModalProps {
  isFirstRun: boolean;
  modelType: string;
  oldScenario: ScenarioNode[];
  newScenario: ScenarioNode[];
  onBackToEditing: () => void;
  onClose: () => void;
  outcomeVariable?: string;
  // ==== HROAS: current model-selection-bar state. When set, revenue totals (direct/induced/
  // actualized, and the by-model panel) are re-derived from baselineByModel/previousByModel
  // rather than read directly off the node — those trees are built once and never rebuilt on a
  // toggle/weight change, so this is what makes Change Impact respect the selection bar
  // instantly. undefined on non-HROAS pages, where the direct fields are used as-is. ====
  includedModelIds?: Set<string>;
  normalizedModelWeights?: Record<string, number>;
  // ==== HROAS: the selected run's model slots (labels for the by-model panel/columns). ====
  hroasModels?: HroasSpModel[];
  // ==== HROAS: backend analyze notices (failed/partial models, plan notes). A failed model's
  // revenue is missing, so its by-model card reads "Revenue missing", never $0. ====
  hroasNotices?: HroasDisplayWarning[];
  hroasFailedModelIds?: Set<string>;
}

const LS_KEY_COLUMN_CONFIG = 'rmmm_change_impact_column_config';
const LS_KEY_SHOW_COLUMN_SETUP = 'rmmm_change_impact_show_column_setup';
const LS_KEY_COMPARE_TO = 'rmmm_change_impact_compare_to';

// ==== HROAS: the model lens must be the default on load. DEFAULT_COLUMN_CONFIG
// is shared across every model type via the same LS_KEY_COLUMN_CONFIG, so it can't be changed
// globally without turning on a meaningless toggle for GeoMMM/rMMM users — this HROAS-only
// variant only applies when no preference has ever been persisted (see the useLocalStorageState
// lazy initializer below). ====
const HROAS_DEFAULT_COLUMN_CONFIG: ColumnConfig = { ...DEFAULT_COLUMN_CONFIG, showByModel: true };

const COMPARE_TO_OPTIONS = [
  { label: 'Previous Scenario', value: 'previous' },
  { label: 'Baseline', value: 'baseline' }
];

// A node's actualized spend at a given stage — mirrors getActualizedMetricValue's canonical
// direct+indirect-reattributedOut formula (ScenarioPlanningUtils.tsx), but reads ScenarioNode's
// differently-prefixed previous*/baseline* fields instead of a ScenarioDateRow, so it can't reuse
// that function directly.
const getNodeActualizedSpend = (
  node: ScenarioNode | undefined,
  stage: 'previous' | 'baseline',
  isRMMMNode: boolean
): number => {
  if (!node) {
    return 0;
  }
  const direct = stage === 'previous' ? node.previousDirectSpend : (node.baselineDirectSpend ?? 0);
  if (!isRMMMNode) {
    return direct;
  }
  const indirect =
    stage === 'previous' ? (node.previousIndirectSpend ?? 0) : (node.baselineIndirectSpend ?? 0);
  const reattributedOut =
    stage === 'previous'
      ? (node.previousReattributedOutSpend ?? 0)
      : (node.baselineReattributedOutSpend ?? 0);
  return direct + indirect - reattributedOut;
};

export const ChangeImpactModal = ({
  isFirstRun,
  modelType,
  oldScenario,
  newScenario,
  onBackToEditing,
  onClose,
  outcomeVariable,
  includedModelIds,
  normalizedModelWeights,
  hroasModels,
  hroasNotices,
  hroasFailedModelIds
}: ChangeImpactModalProps): ReactElement => {
  // ==== HROAS: the Direct/Induced/Actualized stage lens applies to holistic totals too, reused
  // as-is; a holistic tree's nodes already carry summed previousIndirect*/
  // baselineIndirect* from any rMMM/BrandEquity-type instances in the blend, so this flag also
  // unlocks the column-setup bar, which is where the new "By Model" toggle lives. ====
  const isRMMM = modelType === MODEL_TYPES.R_MMM || modelType === MODEL_TYPES.HROAS;
  const isHroas = modelType === MODEL_TYPES.HROAS;
  const effectLabel = getEffectLabel(outcomeVariable);
  const costMetricLabel = getCostMetricLabel(outcomeVariable);
  const [filter, setFilter] = useState<{ filter: (line: DeltaRow) => boolean }>();
  // ==== HROAS: this row-hierarchy dropdown only ever offers Channel/Platform/Tactic
  // (GROUP_BY_OPTIONS below) — unlike the main chart, Change Impact has no Model row-grouping to
  // default into. The "model lens defaults on load" requirement is the By Model
  // toggle/crossed-columns default below, which is the actual model lens for this modal. ====
  const [groupBy, setGroupBy] = useState<GROUP_BY_ENUM>(GROUP_BY_ENUM.CHANNEL);

  // ==== HROAS: By Model defaults on for HROAS the first time this modal is ever
  // opened (no persisted preference yet); once a preference exists (any model type), it wins.
  // The lazy initializer only runs when nothing is stored yet, so an existing preference for any
  // model type is never overridden. ====
  const [columnConfig, setColumnConfig] = useLocalStorageState<ColumnConfig>(
    LS_KEY_COLUMN_CONFIG,
    () => (isHroas ? HROAS_DEFAULT_COLUMN_CONFIG : DEFAULT_COLUMN_CONFIG)
  );
  const [showColumnSetup, setShowColumnSetup] = useLocalStorageState<boolean>(
    LS_KEY_SHOW_COLUMN_SETUP,
    true
  );
  const [compareTo, setCompareTo] = useLocalStorageState<CompareTarget>(
    LS_KEY_COMPARE_TO,
    'previous'
  );

  const effectiveConfig = columnConfig ?? DEFAULT_COLUMN_CONFIG;
  const effectiveCompareTo: CompareTarget = compareTo ?? 'previous';

  const dimHeaders = useMemo(() => getDimensionColumnHeaders(groupBy), [groupBy]);
  const dimCount = dimHeaders.length;

  const newLeafEntries = useMemo(() => collectLeafEntries(newScenario), [newScenario]);
  const oldLeafMap = useMemo(() => {
    const map = new Map<string, ScenarioNode>();
    for (const { node, key } of collectLeafEntries(oldScenario)) {
      map.set(key, node);
    }
    return map;
  }, [oldScenario]);

  const { featuresModified, featuresImpacted } = useMemo(() => {
    let modified = 0;
    let impacted = 0;
    for (const { node: n, key } of newLeafEntries) {
      const old = oldLeafMap.get(key);
      const newSpend = getNodeActualizedSpend(n, 'previous', isRMMM);
      const oldSpend = getNodeActualizedSpend(old, 'previous', isRMMM);
      const changed = old ? Math.abs(newSpend - oldSpend) >= MIN_CHANGE_THRESHOLD : true;
      if (changed) {
        impacted++;
        if (!n.isDemandCapture) {
          modified++;
        }
      }
    }
    return { featuresModified: modified, featuresImpacted: impacted };
  }, [newLeafEntries, oldLeafMap, isRMMM]);

  const flightingRows = useMemo(
    () =>
      newLeafEntries
        .filter(({ node: n, key }) => {
          const old = oldLeafMap.get(key);
          return old ? n.useHistoricalFlighting !== old.useHistoricalFlighting : false;
        })
        .map(({ node: n, key }) => ({
          channel: stripDcDgMarker(n.name),
          prevFlighting: oldLeafMap.get(key)!.useHistoricalFlighting ? 'Historical' : 'Even',
          newFlighting: n.useHistoricalFlighting ? 'Historical' : 'Even'
        })),
    [newLeafEntries, oldLeafMap]
  );

  const oldNodeMap = useMemo(() => {
    const depth = GROUP_BY_EXPAND_DEPTH_MAP[groupBy];
    const visible = getVisibleNodes({
      nodes: oldScenario,
      expandedMap: {},
      sortOption: SORT_BY_ENUM.ALPHABETICALLY,
      sortDirection: SORT_DIRECTION_ENUM.LOW_TO_HIGH,
      defaultExpandDepth: depth
    });
    return new Map(
      visible.filter(({ level }) => level === depth).map(({ node, key }) => [key, node])
    );
  }, [oldScenario, groupBy]);

  const {
    allDeltaRows,
    totalNewSpend,
    totalOldSpend,
    totalNewRevenue,
    totalOldRevenue,
    totalBaselineSpend,
    totalBaselineRevenue
  } = useMemo(() => {
    const depth = GROUP_BY_EXPAND_DEPTH_MAP[groupBy];
    const visible = getVisibleNodes({
      nodes: newScenario,
      expandedMap: {},
      sortOption: SORT_BY_ENUM.ALPHABETICALLY,
      sortDirection: SORT_DIRECTION_ENUM.LOW_TO_HIGH,
      defaultExpandDepth: depth
    });

    const totals = {
      newSpendSum: 0,
      oldSpendSum: 0,
      newRevenueSum: 0,
      oldRevenueSum: 0,
      baselineSpendSum: 0,
      baselineRevenueSum: 0
    };

    // ==== HROAS: re-derive a revenue figure from a node's per-model breakdown, restricted to
    // included models and scaled by weight, instead of trusting the node's own (unweighted,
    // built-once) field — this is what makes Change Impact respect the model-selection bar
    // instantly rather than only on the next "Apply". Falls back to the raw field when the props
    // aren't set (every non-HROAS page), so behavior there is unchanged. ====
    const weightedOrRaw = (
      byModel: ModelRevenueBreakdown | undefined,
      rawValue: number,
      field: keyof ModelRevenueBreakdown[string]
    ): number =>
      byModel && includedModelIds && normalizedModelWeights
        ? sumWeightedByModel(byModel, includedModelIds, normalizedModelWeights, field)
        : rawValue;

    const rows: DeltaRow[] = visible
      .filter(({ level }) => level === depth)
      .map(({ node, level, key }) => {
        const oldNode = oldNodeMap.get(key);

        const newDirectRev = weightedOrRaw(
          node.previousByModel,
          node.previousDirectRevenue,
          'directRevenue'
        );
        const prevDirectRev = weightedOrRaw(
          oldNode?.previousByModel,
          oldNode?.previousDirectRevenue ?? 0,
          'directRevenue'
        );
        const baselineDirectRev = weightedOrRaw(
          node.baselineByModel,
          node.baselineDirectRevenue ?? 0,
          'directRevenue'
        );
        const newIndirectRev = isRMMM
          ? weightedOrRaw(
              node.previousByModel,
              node.previousIndirectRevenue ?? 0,
              'indirectRevenue'
            )
          : 0;
        const prevIndirectRev = isRMMM
          ? weightedOrRaw(
              oldNode?.previousByModel,
              oldNode?.previousIndirectRevenue ?? 0,
              'indirectRevenue'
            )
          : 0;
        const baselineIndirectRev = isRMMM
          ? weightedOrRaw(
              node.baselineByModel,
              node.baselineIndirectRevenue ?? 0,
              'indirectRevenue'
            )
          : 0;
        const newReattributedOutRev = isRMMM
          ? weightedOrRaw(
              node.previousByModel,
              node.previousReattributedOutRevenue ?? 0,
              'reattributedOutRevenue'
            )
          : 0;
        const prevReattributedOutRev = isRMMM
          ? weightedOrRaw(
              oldNode?.previousByModel,
              oldNode?.previousReattributedOutRevenue ?? 0,
              'reattributedOutRevenue'
            )
          : 0;
        const baselineReattributedOutRev = isRMMM
          ? weightedOrRaw(
              node.baselineByModel,
              node.baselineReattributedOutRevenue ?? 0,
              'reattributedOutRevenue'
            )
          : 0;
        // Reattribution is zero-sum: a demand-capture channel's indirect gain elsewhere is offset
        // by an equal reattributedOut loss here. Net it into "induced" once, here, rather than
        // re-subtracting reattributedOut at every downstream consumer (actualized, byModelRevSplit
        // below) — see getActualizedMetricValue for the canonical formula this mirrors.
        const newInducedRev = newIndirectRev - newReattributedOutRev;
        const prevInducedRev = prevIndirectRev - prevReattributedOutRev;
        const baselineInducedRev = baselineIndirectRev - baselineReattributedOutRev;
        const newActualizedRev = newDirectRev + newInducedRev;
        const prevActualizedRev = prevDirectRev + prevInducedRev;
        const baselineActualizedRev = baselineDirectRev + baselineInducedRev;

        const newDirectSpend = node.previousDirectSpend;
        const prevDirectSpend = oldNode?.previousDirectSpend ?? 0;
        const baselineDirectSpend = node.baselineDirectSpend ?? 0;
        const newIndirectSpend = isRMMM ? (node.previousIndirectSpend ?? 0) : 0;
        const prevIndirectSpend = isRMMM ? (oldNode?.previousIndirectSpend ?? 0) : 0;
        const baselineIndirectSpend = isRMMM ? (node.baselineIndirectSpend ?? 0) : 0;
        const newReattributedOutSpend = isRMMM ? (node.previousReattributedOutSpend ?? 0) : 0;
        const prevReattributedOutSpend = isRMMM ? (oldNode?.previousReattributedOutSpend ?? 0) : 0;
        const baselineReattributedOutSpend = isRMMM ? (node.baselineReattributedOutSpend ?? 0) : 0;
        const newInducedSpend = newIndirectSpend - newReattributedOutSpend;
        const prevInducedSpend = prevIndirectSpend - prevReattributedOutSpend;
        const baselineInducedSpend = baselineIndirectSpend - baselineReattributedOutSpend;
        const newActualizedSpend = newDirectSpend + newInducedSpend;
        const prevActualizedSpend = prevDirectSpend + prevInducedSpend;
        const baselineActualizedSpend = baselineDirectSpend + baselineInducedSpend;

        // Noise-suppress sub-threshold movements for the overview card totals
        const effectivePrevRev =
          Math.abs(prevActualizedRev - baselineActualizedRev) < MIN_CHANGE_THRESHOLD
            ? baselineActualizedRev
            : prevActualizedRev;
        const effectivePrevSpend =
          Math.abs(prevActualizedSpend - baselineActualizedSpend) < MIN_CHANGE_THRESHOLD
            ? baselineActualizedSpend
            : prevActualizedSpend;
        const revDelta = newActualizedRev - prevActualizedRev;
        const spendDelta = newActualizedSpend - prevActualizedSpend;
        totals.newRevenueSum +=
          Math.abs(revDelta) < MIN_CHANGE_THRESHOLD ? effectivePrevRev : newActualizedRev;
        totals.newSpendSum +=
          Math.abs(spendDelta) < MIN_CHANGE_THRESHOLD ? effectivePrevSpend : newActualizedSpend;
        totals.oldRevenueSum += effectivePrevRev;
        totals.oldSpendSum += effectivePrevSpend;
        totals.baselineRevenueSum += baselineActualizedRev;
        totals.baselineSpendSum += baselineActualizedSpend;

        // ==== HROAS: per-model actualized-revenue breakdown for the "By Model" panel, split
        // into Direct vs. Indirect per model. Sourced from
        // previousByModel/baselineByModel, which mirror previousDirectRevenue/baselineDirectRevenue
        // but split by contributing model instance AND by revenue component. Each model's own
        // contribution is scaled by its own weight (0 if excluded). The INDIRECT split entry is
        // itself already net of reattributedOut (mirrors the row-level induced netting above), so
        // Direct + Indirect sums to byModelRev's own total, rather than always showing each
        // model's raw, un-reweighted, un-netted share. ====
        let byModelRev: Record<string, MetricValues> | undefined;
        let byModelRevSplit: Record<string, Record<REVENUE_SPLIT, MetricValues>> | undefined;
        if (isHroas) {
          const modelIds = new Set<string>([
            ...Object.keys(node.previousByModel ?? {}),
            ...Object.keys(oldNode?.previousByModel ?? {}),
            ...Object.keys(node.baselineByModel ?? {})
          ]);
          if (modelIds.size > 0) {
            byModelRev = {};
            byModelRevSplit = {};
            for (const modelId of modelIds) {
              const weight = getModelRevenueWeight(
                { modelId },
                includedModelIds,
                normalizedModelWeights
              );
              const newDirectModelRev =
                weight * (node.previousByModel?.[modelId]?.directRevenue ?? 0);
              const newIndirectModelRev =
                weight * (node.previousByModel?.[modelId]?.indirectRevenue ?? 0);
              const newReattributedOutModelRev =
                weight * (node.previousByModel?.[modelId]?.reattributedOutRevenue ?? 0);
              const prevDirectModelRev =
                weight * (oldNode?.previousByModel?.[modelId]?.directRevenue ?? 0);
              const prevIndirectModelRev =
                weight * (oldNode?.previousByModel?.[modelId]?.indirectRevenue ?? 0);
              const prevReattributedOutModelRev =
                weight * (oldNode?.previousByModel?.[modelId]?.reattributedOutRevenue ?? 0);
              const baselineDirectModelRev =
                weight * (node.baselineByModel?.[modelId]?.directRevenue ?? 0);
              const baselineIndirectModelRev =
                weight * (node.baselineByModel?.[modelId]?.indirectRevenue ?? 0);
              const baselineReattributedOutModelRev =
                weight * (node.baselineByModel?.[modelId]?.reattributedOutRevenue ?? 0);

              const newInducedModelRev = newIndirectModelRev - newReattributedOutModelRev;
              const prevInducedModelRev = prevIndirectModelRev - prevReattributedOutModelRev;
              const baselineInducedModelRev =
                baselineIndirectModelRev - baselineReattributedOutModelRev;

              byModelRevSplit[modelId] = {
                [REVENUE_SPLIT.DIRECT]: buildMetricValues(
                  newDirectModelRev,
                  prevDirectModelRev,
                  baselineDirectModelRev
                ),
                [REVENUE_SPLIT.INDIRECT]: buildMetricValues(
                  newInducedModelRev,
                  prevInducedModelRev,
                  baselineInducedModelRev
                )
              };
              // Direct + (already-netted) Indirect equals this model's own actualized total.
              byModelRev[modelId] = buildMetricValues(
                newDirectModelRev + newInducedModelRev,
                prevDirectModelRev + prevInducedModelRev,
                baselineDirectModelRev + baselineInducedModelRev
              );
            }
          }
        }

        return {
          name: stripDcDgMarker(node.name),
          hierarchyParts: key.split(', ').map(stripDcDgMarker),
          level,
          actualizedRev: buildMetricValues(
            newActualizedRev,
            prevActualizedRev,
            baselineActualizedRev
          ),
          inducedRev: buildMetricValues(newInducedRev, prevInducedRev, baselineInducedRev),
          directRev: buildMetricValues(newDirectRev, prevDirectRev, baselineDirectRev),
          actualizedSpend: buildMetricValues(
            newActualizedSpend,
            prevActualizedSpend,
            baselineActualizedSpend
          ),
          inducedSpend: buildMetricValues(newInducedSpend, prevInducedSpend, baselineInducedSpend),
          directSpend: buildMetricValues(newDirectSpend, prevDirectSpend, baselineDirectSpend),
          byModelRev,
          byModelRevSplit
        };
      });

    return {
      allDeltaRows: rows,
      totalNewSpend: totals.newSpendSum,
      totalOldSpend: totals.oldSpendSum,
      totalNewRevenue: totals.newRevenueSum,
      totalOldRevenue: totals.oldRevenueSum,
      totalBaselineSpend: totals.baselineSpendSum,
      totalBaselineRevenue: totals.baselineRevenueSum
    };
  }, [groupBy, newScenario, oldNodeMap, isRMMM, isHroas, includedModelIds, normalizedModelWeights]);

  const deltaRows = useMemo(
    () => (filter ? allDeltaRows.filter(r => filter.filter(r)) : allDeltaRows),
    [filter, allDeltaRows]
  );

  const newROAS = totalNewSpend > 0 ? totalNewRevenue / totalNewSpend : 0;
  const oldROAS = totalOldSpend > 0 ? totalOldRevenue / totalOldSpend : 0;
  const baselineROAS = totalBaselineSpend > 0 ? totalBaselineRevenue / totalBaselineSpend : 0;

  // ==== HROAS: plan-level actualized revenue per model, for the "By Model" panel. Sums
  // allDeltaRows' byModelRev (unfiltered — matches the other overview cards, which are also
  // whole-plan totals regardless of the row filter/sort). ====
  const byModelTotals = useMemo(() => {
    const totals: Record<string, { newValue: number; oldValue: number; baselineValue: number }> =
      {};
    if (!isHroas) {
      return totals;
    }
    for (const row of allDeltaRows) {
      for (const [modelId, mv] of Object.entries(row.byModelRev ?? {})) {
        const acc = totals[modelId] ?? { newValue: 0, oldValue: 0, baselineValue: 0 };
        acc.newValue += mv.newValue;
        acc.oldValue += mv.prevValue;
        acc.baselineValue += mv.baselineValue;
        totals[modelId] = acc;
      }
    }
    // Per-row values are raw, so sub-threshold model noise survives the sum; suppress it on the
    // model total as the overview cards above do.
    return Object.fromEntries(
      Object.entries(totals).map(([modelId, acc]) => [modelId, suppressSubThresholdModelNoise(acc)])
    );
  }, [allDeltaRows, isHroas]);

  // ==== HROAS: crossed by-model columns — same run's model slots the
  // by-model summary panel above already uses, so an excluded model still gets a (zero-valued)
  // column instead of the table's shape shifting as models are toggled. ====
  const byModelColumnModels = useMemo(
    () => (isHroas ? (hroasModels ?? []) : []),
    [isHroas, hroasModels]
  );

  const columnDefs = useMemo(
    () =>
      buildColumnDefs(
        effectiveConfig,
        isRMMM,
        effectiveCompareTo,
        effectLabel,
        byModelColumnModels
      ),
    [effectiveConfig, isRMMM, effectiveCompareTo, effectLabel, byModelColumnModels]
  );

  const superTopHeaders = useMemo(
    () =>
      isRMMM
        ? buildSuperTopHeaders(columnDefs, effectLabel)
        : [
            { span: 2, data: 'Spend' },
            { span: 2, data: effectLabel }
          ],
    [isRMMM, columnDefs, effectLabel]
  );

  const midTopHeaders = useMemo(() => buildMidTopHeaders(columnDefs), [columnDefs]);

  const topHeaders = useMemo(() => buildTopHeaders(columnDefs), [columnDefs]);

  const [sortColumn, setSortColumn] = useState<number | null>(null);
  const [sortAscending, setSortAscending] = useState(true);

  const getDimSortIndex = useCallback((dimIndex: number): number => -(dimIndex + 2), []);

  const toggleSort = useCallback(
    (colIndex: number) => {
      if (sortColumn === colIndex) {
        if (sortAscending) {
          setSortAscending(false);
        } else {
          setSortColumn(null);
          setSortAscending(true);
        }
      } else {
        setSortColumn(colIndex);
        setSortAscending(true);
      }
    },
    [sortColumn, sortAscending]
  );

  const sortedRows = useMemo(() => {
    if (sortColumn === null) {
      return deltaRows;
    }
    const sorted = [...deltaRows].sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;
      if (sortColumn === DIMENSION_SORT_INDEX) {
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
      } else if (sortColumn < DIMENSION_SORT_INDEX) {
        const partIndex = -(sortColumn + 2) + 1;
        aVal = (a.hierarchyParts[partIndex] ?? '').toLowerCase();
        bVal = (b.hierarchyParts[partIndex] ?? '').toLowerCase();
      } else {
        aVal = columnDefs[sortColumn]?.getValue(a) ?? 0;
        bVal = columnDefs[sortColumn]?.getValue(b) ?? 0;
      }
      if (aVal < bVal) {
        return sortAscending ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortAscending ? 1 : -1;
      }
      return 0;
    });
    return sorted;
  }, [deltaRows, sortColumn, sortAscending, columnDefs]);

  const totalLeftWidth = LEFT_COLUMN_WIDTH + dimCount * DIMENSION_COLUMN_WIDTH;

  const leftData = useMemo(
    () =>
      sortedRows.map(row => {
        const parts = [row.hierarchyParts[0]];
        for (let i = 0; i < dimCount; i++) {
          parts.push(row.hierarchyParts[i + 1] ?? '--');
        }
        return parts;
      }),
    [sortedRows, dimCount]
  );

  interface GridCell {
    value: number;
    colorPct: number;
    isAbbreviated: boolean;
    tooltip: CellTooltipData;
  }

  const rightGridData: GridCell[][] = useMemo(
    () =>
      sortedRows.map(row =>
        columnDefs.map(col => ({
          value: col.getValue(row),
          colorPct: col.getColorPct(row),
          isAbbreviated: col.isAbbreviated,
          tooltip: col.getTooltipData(row)
        }))
      ),
    [sortedRows, columnDefs]
  );

  const handleCsvExport = useCallback(() => {
    const csvData = sortedRows.map(row => {
      const deduped = dedupeHierarchyParts([
        row.hierarchyParts[0],
        ...dimHeaders.map((_, i) => row.hierarchyParts[i + 1])
      ]);
      const dimColumns: Record<string, string> = { Channel: deduped[0] };
      dimHeaders.forEach((header, i) => {
        dimColumns[header] = deduped[i + 1];
      });
      const dataCols: Record<string, string> = {};
      columnDefs.forEach(col => {
        // A by-model column reuses groupType 'actualized' for every model, so label it by model
        // name + revenue split instead — otherwise every model's (and every split's) column would
        // export under the same header, silently colliding and overwriting each other. Skip the
        // metricLabel suffix there too: by-model columns are always revenue, and the model's own
        // label (e.g. "Brand Equity · Total Revenue") may already say so.
        const header = col.modelId
          ? `${col.modelLabel} - ${REVENUE_SPLIT_LABEL[col.revenueSplit ?? REVENUE_SPLIT.DIRECT]} ${col.label}`
          : `${isRMMM ? `${capitalize(col.groupType)} ` : ''}${col.metric === 'rev' ? effectLabel : 'Spend'} ${col.label}`;
        dataCols[header] = col.isAbbreviated
          ? abbreviateValue(col.getValue(row))
          : toPrettyPercent(col.getValue(row));
      });
      return { ...dimColumns, ...dataCols };
    });
    downloadJSONToCSV(csvData, 'ScenarioPlanningDelta');
  }, [sortedRows, dimHeaders, columnDefs, effectLabel, isRMMM]);

  const SortIndicator = useCallback<(props: { colIndex: number }) => ReactElement>(
    ({ colIndex }) => {
      const isActive = sortColumn === colIndex;
      let Icon = MdSwapVert;
      if (isActive) {
        Icon = sortAscending ? MdArrowUpward : MdArrowDownward;
      }
      return (
        <div className={cn('sort-icon', { active: isActive })}>
          <Icon size={12} />
        </div>
      );
    },
    [sortColumn, sortAscending]
  );

  const cornerRenderer = useCallback(
    (location: string) => {
      if (location === 'nw') {
        return (
          <div className='corner-labels'>
            <InteractiveDiv
              className={cn('corner-label', { 'corner-border-right': dimHeaders.length > 0 })}
              style={{ width: LEFT_COLUMN_WIDTH }}
              onClick={e => {
                e.stopPropagation();
                toggleSort(DIMENSION_SORT_INDEX);
              }}
            >
              <span>Channel</span>
              <SortIndicator colIndex={DIMENSION_SORT_INDEX} />
            </InteractiveDiv>
            {dimHeaders.map((header, i) => (
              <InteractiveDiv
                key={header}
                className={cn('corner-label', { 'corner-border-right': i < dimHeaders.length - 1 })}
                style={{ width: DIMENSION_COLUMN_WIDTH }}
                onClick={e => {
                  e.stopPropagation();
                  toggleSort(getDimSortIndex(i));
                }}
              >
                <span>{header}</span>
                <SortIndicator colIndex={getDimSortIndex(i)} />
              </InteractiveDiv>
            ))}
          </div>
        );
      }
      return <div />;
    },
    [toggleSort, SortIndicator, dimHeaders, getDimSortIndex]
  );

  const topRenderer = useCallback<CellRenderer<string>>(
    ({ data, columnIndex, style, classes }) => (
      <InteractiveDiv
        style={style}
        className={cn(classes, 'sortable-header')}
        onClick={() => toggleSort(columnIndex)}
      >
        <span>{data}</span>
        <SortIndicator colIndex={columnIndex} />
      </InteractiveDiv>
    ),
    [toggleSort, SortIndicator]
  );

  const leftRenderer = useCallback<CellRenderer<string[]>>(({ data, style, classes }) => {
    const deduped = dedupeHierarchyParts(data);
    const subDims = deduped.slice(1);
    const isLastChannel = subDims.length === 0;
    return (
      <div className={cn(classes)} style={style}>
        <div className='dimension-row'>
          <div
            className={cn('dimension-line', { 'dim-border-right': !isLastChannel })}
            style={{ width: LEFT_COLUMN_WIDTH }}
          >
            {deduped[0]}
          </div>
          {subDims.map((part, i) => (
            <div
              key={i}
              className={cn('dimension-line', { 'dim-border-right': i < subDims.length - 1 })}
              style={{ width: DIMENSION_COLUMN_WIDTH }}
            >
              {part}
            </div>
          ))}
        </div>
      </div>
    );
  }, []);

  const cellRenderer = useCallback<CellRenderer<GridCell>>(({ data, style, classes }) => {
    const isSubDollar = data.isAbbreviated && Math.abs(data.value) < MIN_CHANGE_THRESHOLD;
    const color = isSubDollar ? Neutral500 : getIntensityColor(data.colorPct);
    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        placement='auto'
        overlay={
          <Popover id='cell-detail-popover' className='cell-detail-popover'>
            <Popover.Body>
              <CellDetailTooltip data={data.tooltip} />
            </Popover.Body>
          </Popover>
        }
      >
        <div className={cn(classes, 'col-border-right')} style={style}>
          <div style={{ color }}>
            {data.isAbbreviated ? abbreviateValue(data.value) : toPrettyPercent(data.value)}
          </div>
        </div>
      </OverlayTrigger>
    );
  }, []);

  const superTopRenderer = useCallback<CellRenderer<string>>(
    ({ data, style, classes }) => (
      <div className={cn(classes, 'bpm-super-header')} style={style}>
        {data}
      </div>
    ),
    []
  );

  const toggleConfigKey = useCallback(
    (key: keyof ColumnConfig) => {
      setColumnConfig(prev => {
        const base = prev ?? DEFAULT_COLUMN_CONFIG;
        return { ...base, [key]: !base[key] };
      });
    },
    [setColumnConfig]
  );

  const MAX_TABLE_HEIGHT = 354;
  const naturalTableHeight =
    SUPER_TOP_HEIGHT +
    (midTopHeaders ? SUPER_TOP_HEIGHT : 0) +
    HEADER_HEIGHT +
    sortedRows.length * BASE_ROW_HEIGHT;
  const tableHeight = Math.min(naturalTableHeight, MAX_TABLE_HEIGHT);

  const SUB_COLUMN_KEYS: (keyof ColumnConfig)[] = [
    'showPctChange',
    'showActualChange',
    'showPrevScenarioPct',
    'showPrevScenarioDelta',
    'showBaseline'
  ];
  const GROUP_KEYS: (keyof ColumnConfig)[] = ['showActualized', 'showDirect', 'showInduced'];
  const activeSubColumnCount = SUB_COLUMN_KEYS.filter(k => effectiveConfig[k]).length;
  const activeGroupCount = GROUP_KEYS.filter(k => effectiveConfig[k]).length;

  return (
    <Modal
      show
      onHide={onClose}
      centered
      size='lg'
      className='change-impact-modal'
      data-testid='change-impact-modal'
    >
      <Modal.Header>
        <div className='title-row'>
          <div className='title'>
            {isFirstRun ? 'New Scenario vs Business As Usual' : 'New Scenario vs Previous Scenario'}
          </div>
          <Button
            size={Size.SM}
            design={Design.NEUTRAL}
            leadingElement={<MdOutlineFileDownload />}
            onClick={handleCsvExport}
          />
          <Button
            size={Size.SM}
            design={Design.NEUTRAL}
            leadingElement={<MdClose />}
            onClick={onClose}
          />
        </div>
        <div className='pills-row'>
          <span className='pill'>{featuresModified} features modified</span>
          <span className='pill'>{featuresImpacted} features impacted</span>
          <span className='pill'>{flightingRows.length} flighting changes</span>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className='metric-cards'>
          <OverviewMetric
            title={isRMMM ? 'Total Spend Impact' : 'Total Spend'}
            baselineValue={totalBaselineSpend}
            oldValue={totalOldSpend}
            newValue={totalNewSpend}
            prefix={CURRENCY_PREFIX}
            isFirstRun={isFirstRun}
          />
          <OverviewMetric
            title={isRMMM ? `Total ${effectLabel} Impact` : `Total ${effectLabel}`}
            baselineValue={totalBaselineRevenue}
            oldValue={totalOldRevenue}
            newValue={totalNewRevenue}
            prefix={CURRENCY_PREFIX}
            isFirstRun={isFirstRun}
          />
          <OverviewMetric
            title={isRMMM ? `Total ${costMetricLabel} Impact` : `Overall ${costMetricLabel}`}
            baselineValue={baselineROAS}
            oldValue={oldROAS}
            newValue={newROAS}
            prefix=''
            isFirstRun={isFirstRun}
          />
        </div>

        {/* ==== HROAS: by-model contribution panel — how much of the plan's actualized revenue
            each included model instance accounts for. Reuses the same OverviewMetric card as
            the totals above. ==== */}
        {isHroas && effectiveConfig.showByModel && (
          <div className='metric-cards by-model-cards'>
            {(hroasModels ?? []).map(model => {
              if (hroasFailedModelIds?.has(model.id)) {
                return (
                  <div key={model.id} className='by-model-missing'>
                    <strong>{model.label}</strong>
                    <span>Revenue missing (model failed)</span>
                  </div>
                );
              }
              const totals = byModelTotals[model.id] ?? {
                newValue: 0,
                oldValue: 0,
                baselineValue: 0
              };
              return (
                <OverviewMetric
                  key={model.id}
                  title={model.label}
                  baselineValue={totals.baselineValue}
                  oldValue={totals.oldValue}
                  newValue={totals.newValue}
                  prefix={CURRENCY_PREFIX}
                  isFirstRun={isFirstRun}
                />
              );
            })}
          </div>
        )}

        {isHroas && (hroasNotices ?? []).length > 0 && (
          <div className='hroas-notices'>
            {(hroasNotices ?? []).map(notice => (
              <div key={`${notice.modelId}-${notice.message}`} className='hroas-notice-row'>
                <strong>{notice.label ?? notice.modelId}</strong> {notice.message}
                {notice.detail && <div className='hroas-notice-detail'>{notice.detail}</div>}
              </div>
            ))}
          </div>
        )}

        <div className='toolbar'>
          <FilterBar
            onFilter={filterFn => setFilter({ filter: filterFn })}
            lines={deltaRows}
            options={['name']}
            size={Size.LG}
          />
          {isRMMM && !isFirstRun && (
            <Dropdown
              overlayPlacement={Placement.RIGHT}

              stretch={StretchMode.FIT}
              label='Compare to'
              onChange={val => setCompareTo(val as CompareTarget)}
              options={COMPARE_TO_OPTIONS}
              size={Size.SM}
              value={effectiveCompareTo}
            />
          )}
          <Dropdown
            overlayPlacement={Placement.RIGHT}

            stretch={StretchMode.FIT}
            label='Group by'
            onChange={setGroupBy}
            options={GROUP_BY_OPTIONS}
            size={Size.SM}
            value={groupBy}
          />
          {isRMMM && (
            <Button
              className={(showColumnSetup ?? true) ? 'gear-btn-active' : ''}
              size={Size.LG}
              design={Design.NEUTRAL}
              leadingElement={<MdSettings />}
              onClick={() => setShowColumnSetup(prev => !(prev ?? true))}
              appearance={Appearance.EMPTY}
            />
          )}
        </div>

        {isRMMM && (showColumnSetup ?? true) && (
          <div className='column-setup-bar'>
            <span className='column-setup-label'>Column Setup:</span>
            <ToggleSwitch
              label='Actualized'
              checked={effectiveConfig.showActualized}
              disabled={effectiveConfig.showActualized && activeGroupCount === 1}
              onChange={_ => toggleConfigKey('showActualized')}
              size={Size.SM}
            />
            <ToggleSwitch
              label='% Change'
              checked={effectiveConfig.showPctChange}
              disabled={effectiveConfig.showPctChange && activeSubColumnCount === 1}
              onChange={_ => toggleConfigKey('showPctChange')}
              size={Size.SM}
            />
            <ToggleSwitch
              label='Actual Change'
              checked={effectiveConfig.showActualChange}
              disabled={effectiveConfig.showActualChange && activeSubColumnCount === 1}
              onChange={_ => toggleConfigKey('showActualChange')}
              size={Size.SM}
            />
            <ToggleSwitch
              label='Direct'
              checked={effectiveConfig.showDirect}
              disabled={effectiveConfig.showDirect && activeGroupCount === 1}
              onChange={_ => toggleConfigKey('showDirect')}
              size={Size.SM}
            />
            <ToggleSwitch
              label='Induced'
              checked={effectiveConfig.showInduced}
              disabled={effectiveConfig.showInduced && activeGroupCount === 1}
              onChange={_ => toggleConfigKey('showInduced')}
              size={Size.SM}
            />
            {/* ==== HROAS: by-model contribution toggle — drives both the summary
                panel above and the crossed by-model columns in the table below (buildColumnDefs'
                byModelColumnModels), so turning it off hides both instead of leaving the table
                showing model columns nobody asked for. ==== */}
            {isHroas && (
              <ToggleSwitch
                label='By Model'
                checked={effectiveConfig.showByModel}
                onChange={_ => toggleConfigKey('showByModel')}
                size={Size.SM}
              />
            )}
            <span className='column-setup-optional'>
              <ToggleSwitch
                label='Previous Scenario % Change?'
                checked={effectiveConfig.showPrevScenarioPct}
                disabled={effectiveConfig.showPrevScenarioPct && activeSubColumnCount === 1}
                onChange={_ => toggleConfigKey('showPrevScenarioPct')}
                size={Size.SM}
              />
            </span>
            <span className='column-setup-optional'>
              <ToggleSwitch
                label='Previous Scenario Actual Change?'
                checked={effectiveConfig.showPrevScenarioDelta}
                disabled={effectiveConfig.showPrevScenarioDelta && activeSubColumnCount === 1}
                onChange={_ => toggleConfigKey('showPrevScenarioDelta')}
                size={Size.SM}
              />
            </span>
            <span className='column-setup-optional'>
              <ToggleSwitch
                label='Baseline?'
                checked={effectiveConfig.showBaseline}
                disabled={effectiveConfig.showBaseline && activeSubColumnCount === 1}
                onChange={_ => toggleConfigKey('showBaseline')}
                size={Size.SM}
              />
            </span>
          </div>
        )}

        <div className='table-container' style={{ height: tableHeight }}>
          <AutoSizer
            renderProp={({ width = 0, height = 0 }) => {
              const colCount = columnDefs.length;
              const availableWidth = width - totalLeftWidth;
              const evenCol = Math.floor(availableWidth / colCount);
              // Fill the available width EXACTLY so the grid lines reach the table's right
              // edge (no gutter): give each column the floored width and add the leftover
              // pixels to the last column. When columns can't fit (below MIN_COLUMN_WIDTH),
              // fall back to equal MIN-width columns and let the table scroll horizontally.
              const columnWidth =
                evenCol >= MIN_COLUMN_WIDTH
                  ? (i: number) =>
                      i === colCount - 1 ? availableWidth - evenCol * (colCount - 1) : evenCol
                  : MIN_COLUMN_WIDTH;
              return (
                <StickyTable
                  data={rightGridData}
                  width={width}
                  height={height}
                  rowHeight={BASE_ROW_HEIGHT}
                  cellRenderer={cellRenderer}
                  columnWidth={columnWidth}
                  topData={topHeaders}
                  topRenderer={topRenderer}
                  topHeight={HEADER_HEIGHT}
                  superTopData={superTopHeaders}
                  superTopHeight={SUPER_TOP_HEIGHT}
                  superTopRenderer={superTopRenderer}
                  leftData={leftData}
                  leftRenderer={leftRenderer}
                  leftWidth={totalLeftWidth}
                  cornerRenderer={cornerRenderer}
                  alternateColors={false}
                />
              );
            }}
          />
        </div>

        {flightingRows.length > 0 && <FlightingChangesTable rows={flightingRows} />}
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={onBackToEditing} appearance={Appearance.OUTLINED} design={Design.NEUTRAL}>
          Back to Editing
        </Button>
        <Button onClick={onClose}>Continue</Button>
      </Modal.Footer>
    </Modal>
  );
};
